import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  Input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy } from '@ngneat/until-destroy';
import { finalize, mergeMap, Observable, of } from 'rxjs';

import { GenerateTableDataRequest, TableInfo } from '@blaze-shared/api-contracts-domain';
import { TablesApiService } from '@blaze-shared/datasets-data-access';
import { BlazeTable } from '@blaze-shared/datasets-domain';
import { JobsFacade } from '@blaze-shared/jobs-data-access';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetColumn, isLinkedColumn, isLinkedInlineColumn } from '@blaze-shared/schema-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import {
  ButtonComponent,
  ButtonIconComponent,
  CheckboxComponent,
  ElementLoadingIndicatorComponent,
  ScrollerComponent,
} from '@blaze-shared/shared-ui';

const FormatToNameMap: Record<string, string> = {
  DateTime: 'Date Time',
  PhoneNumber: 'Phone Number',
  RichText: 'Rich Text',
  GeoPoint: 'Map Coordinates',
  SecureText: 'Secure Text',
  SingleLine: 'Text',
  String: 'Text',
  TimeZone: 'Time Zone',
  ZipCode: 'Zip Code',
};
@UntilDestroy()
@Component({
  selector: 'blaze-table-data-generation-panel',
  templateUrl: './table-data-generation-panel.component.html',
  styleUrls: ['./table-data-generation-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonIconComponent,
    ButtonComponent,
    CheckboxComponent,
    ElementLoadingIndicatorComponent,
    FormsModule,
    MatIcon,
    ReactiveFormsModule,
    ScrollerComponent,
  ],
})
export class TableDataGenerationPanelComponent implements OnInit {
  private readonly dialogService = inject(DialogService);
  private readonly injector = inject(Injector);
  private readonly jobsFacade = inject(JobsFacade);
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);
  private readonly tablesApiService = inject(TablesApiService);

  closePanel = output<boolean>();
  dataReload = output<void>();

  @Input() config$!: Observable<{ config?: BlazeTable }>;

  private readonly linkedDatasets = signal<
    Record<string, { datasetId: string; datasetName: string }>
  >({});
  private readonly projectId = signal<string | undefined>(undefined);

  requestInProgress = signal(false);

  generalDescription = '';
  numberOfRows = signal(10);
  fieldSpecificForm = new FormGroup<{
    [key: string]: FormGroup<{ [key: string]: FormControl<string> }>;
  }>({});
  enabledDatasets = new FormGroup<{ [key: string]: FormControl<boolean | null> }>({});

  readonly columnsInformation = signal<
    Map<TableInfo['id'], { datasetId: string; columnName: string; format: string }[]>
  >(new Map());

  readonly selectedColumnsInformation = computed(() => {
    const selectedDatasetId = this.selectedDatasetId();
    if (!selectedDatasetId) {
      return [];
    }
    return this.columnsInformation().get(selectedDatasetId) || [];
  });

  readonly availableLinkedDatasets = computed(() => {
    return Object.values(this.linkedDatasets()).filter(Boolean);
  });

  readonly selectedFormGroup = computed(() => {
    const datasetId = this.selectedDatasetId();
    if (!datasetId) {
      return new UntypedFormGroup({});
    }
    return this.fieldSpecificForm.get(datasetId) as FormGroup;
  });

  readonly selectedDatasetId = signal<string | undefined>(undefined);

  /** Main dataset Id */
  readonly datasetId = signal<string | undefined>(undefined);
  /** Main dataset Name */
  readonly datasetName = signal<string | undefined>(undefined);

  fillTableDisabled = computed(() => {
    const numberOfRows = this.numberOfRows();
    return this.requestInProgress() || !numberOfRows || numberOfRows < 1;
  });

  ngOnInit(): void {
    this.config$.subscribe((table) => {
      if (!table.config) {
        return;
      }

      this.datasetId.set(table.config.id);
      this.datasetName.set(table.config.name);
      this.projectId.set(table.config.projectId);

      this.configureFormGroupForColumns(table.config.id, table.config.columns);
      this.enabledDatasets.get(table.config.id)?.setValue(true);
      this.selectedDatasetId.set(table.config.id);

      this.linkedDatasets.set(
        Object.fromEntries(
          table.config.columns
            .filter((col) => isLinkedColumn(col) && !isLinkedInlineColumn(col))
            .map((col) => {
              const datasetId = col.link?.tableId;
              const datasetName = col.link?.tableName;
              if (!datasetId) {
                return null;
              }
              return [datasetId, { datasetId, datasetName: datasetName ?? datasetId }];
            })
            .filter((x): x is [string, { datasetId: string; datasetName: string }] => !!x),
        ),
      );
    });

    effect(
      () => {
        Object.values(this.linkedDatasets()).forEach(({ datasetId }) => {
          const existingGroup = this.fieldSpecificForm.get(datasetId);
          if (existingGroup) {
            return;
          }
          const schema = this.schemaService.getDatasetSchema(datasetId);
          if (!schema) {
            return;
          }
          this.configureFormGroupForColumns(datasetId, schema.columns);
        });
      },
      { injector: this.injector },
    );
  }

  fillTable(): void {
    const projectId = this.projectId();
    if (!projectId || !this.numberOfRows) {
      return;
    }
    const colPrompts = this.prepareColumnPrompts();
    if (!colPrompts?.datasetId) {
      return;
    }
    const { datasetId, columnPrompts, linkedDatasetsColumnPrompts } = colPrompts;
    const subDescription = [
      this.datasetName(),
      ...Object.keys(linkedDatasetsColumnPrompts || {}).map(
        (dsId) => this.linkedDatasets()[dsId].datasetName,
      ),
    ]
      .filter(Boolean)
      .join(', ');
    const dialogRef = this.dialogService.openGenerateDialog({
      title: 'Generating Sample Data',
      description: `We're generating sample data for your tables that you selected.\nHold tight while we update your tables:`,
      subDescription,
    });

    this.requestInProgress.set(true);

    this.tablesApiService
      .generateBlazeTableData({
        allowPolling: true,
        columnPrompts,
        datasetId,
        linkedDatasetsColumnPrompts,
        numberOfRows: this.numberOfRows(),
        projectId,
        tableLevelPrompt: this.generalDescription,
      })
      .pipe(
        mergeMap((generateDataJob) => {
          if (generateDataJob.result) {
            return of(generateDataJob.result);
          }
          return this.jobsFacade.pollJobStatusUntilDoneOrStopped(generateDataJob.jobId, 1000);
        }),
        finalize(() => {
          this.requestInProgress.set(false);
          dialogRef.close();
        }),
      )
      .subscribe({
        next: (result) => {
          if (result?.errorMessage) {
            this.notificationService.error(result.errorMessage);
          }
          this.dataReload.emit();
          this.closePanel.emit(true);
        },
        error: (err) => {
          const error = err.error;
          let notificationMessage = `Data could not be generated.`;
          if (error.columnName) {
            notificationMessage = `Data could not be generated because the field "${error.columnName}" has an error: ${error.errorMessage}`;
          } else if (error.errorMessage) {
            notificationMessage = `Data could not be generated. ${error.errorMessage}`;
          }
          this.notificationService.error(notificationMessage);
        },
      });
  }

  private configureFormGroupForColumns(
    datasetId: TableInfo['id'],
    datasetColumns: DatasetColumn[],
  ): void {
    const group = new FormGroup({});
    const formatMap: { datasetId: string; columnName: string; format: string }[] = [];

    datasetColumns
      .filter((col) => !col.isComputed && !col.isInternal && !col.isProtected && !col.isInline)
      .forEach((col) => {
        let format: string = (col.format?.formatType ?? col.dataType) as string;
        if (format && FormatToNameMap[format]) {
          format = FormatToNameMap[format];
        }
        formatMap.push({ datasetId, columnName: col.name, format });
        group.addControl(col.name, new FormControl(''));
      });

    if (formatMap.length) {
      this.columnsInformation.set(this.columnsInformation().set(datasetId, formatMap));
      this.fieldSpecificForm.addControl(datasetId, group);
      this.enabledDatasets.addControl(datasetId, new FormControl(null));
    } else {
      this.linkedDatasets.update((current) => {
        const updated = { ...current };
        delete updated[datasetId];
        return updated;
      });
    }
  }

  private prepareColumnPrompts():
    | {
        datasetId: string;
        columnPrompts: GenerateTableDataRequest['columnPrompts'];
        linkedDatasetsColumnPrompts: GenerateTableDataRequest['linkedDatasetsColumnPrompts'];
      }
    | undefined {
    const datasetId = this.datasetId();
    if (!datasetId) {
      return;
    }
    let linkedDatasetsColumnPrompts:
      | GenerateTableDataRequest['linkedDatasetsColumnPrompts']
      | undefined = this.fieldSpecificForm.getRawValue();
    const columnPrompts = linkedDatasetsColumnPrompts[datasetId] || {};

    delete linkedDatasetsColumnPrompts[datasetId];
    Object.entries(this.enabledDatasets.value).forEach(([dsId, isEnabled]) => {
      if (!isEnabled && linkedDatasetsColumnPrompts?.[dsId]) {
        delete linkedDatasetsColumnPrompts[dsId];
      }
    });
    if (!Object.values(linkedDatasetsColumnPrompts).length) {
      linkedDatasetsColumnPrompts = undefined;
    }

    return { datasetId, columnPrompts, linkedDatasetsColumnPrompts };
  }
}
